import React, { type Dispatch, type SetStateAction } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { getVintedIntegrations } from "src/store/plaidIntegration/selector";
import {
  getVintedIntegration,
  vintedIntegrationCreate,
} from "src/apiService/modules/vinted";
import { getVintedUser } from "src/utils/extension/vinted";
import {
  MissingExtensionError,
  OutdatedExtensionError,
} from "src/utils/extension";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import type { DialogState } from "../types";
import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";

import Row, { VintedNoticeNode } from "./Row";

interface VintedProps {
  hideInventory: boolean;
  setDialog: Dispatch<SetStateAction<DialogState>>;
}

function Vinted({ setDialog, hideInventory }: VintedProps) {
  const recordEvent = useRecordEvent();
  const vintedIntegrations = useSelector(getVintedIntegrations);

  const handleVintedAdd = async () => {
    try {
      const data = await getVintedUser();
      setDialog({
        type: "vinted-confirm",
        props: {
          username: data.username,
          platform: "Vinted",
          onConfirm: async (bankAccountId?: string | undefined) => {
            if (data?.cookies) {
              const existingVintedIntegration = await getVintedIntegration();
              if (existingVintedIntegration?.id) {
                toast.info("That Vinted account is already connected.");
                return;
              }
              await vintedIntegrationCreate({
                cookies: data.cookies,
                username: data.username,
                userId: data.userId,
                lastSync: new Date().toISOString(),
                bankAccountId,
              });
              recordEvent({
                type: "feature-adoption",
                feature: "integration-added",
                element: "sale-platform-vinted",
              });
            }
            setDialog({});
          },
        },
      });
    } catch (e) {
      if (e instanceof MissingExtensionError) {
        setDialog({
          type: "vinted-add-extension",
          props: { platform: "Vinted" },
        });
        return;
      }
      if (e instanceof OutdatedExtensionError) {
        setDialog({
          type: "vinted-add-extension",
          props: { platform: "Vinted", update: true },
        });
        return;
      }
      toast.error((e as Error).message);
    }
  };

  if (vintedIntegrations.length === 0)
    return (
      <PlatformIntegrationsTableRow
        name="Vinted"
        platform={VintedNoticeNode}
        isRunning={false}
        sync={false}
        inventory={false}
        onImport={handleVintedAdd}
        hideInventory={hideInventory}
        onPullDataNow={async () => handleVintedAdd()}
        onSyncChanged={handleVintedAdd}
        onAdd={handleVintedAdd}
      />
    );

  return (
    <>
      {vintedIntegrations.map((integration) => (
        <Row
          key={integration.id}
          integration={integration}
          hideInventory={hideInventory}
          setDialog={setDialog}
        />
      ))}
    </>
  );
}

export default Vinted;
